import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Typography, Card, Divider, Row, Col, Select, DatePicker, Button, message, Space } from 'antd';
import './Accounting.css';
import { useReactToPrint } from 'react-to-print';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;

const formatNumber = (number, locale) => {
  const parsedNumber = parseFloat(number);
  if (isNaN(parsedNumber)) {
    return '0.00';
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(parsedNumber);
};

const formatDate = (dateString, locale) => {
  const date = new Date(dateString);
  return moment(date).format('DD MMM YY');
};

const InvoiceModal = ({ visible, onClose, registration }) => {
  const { t, i18n } = useTranslation();
  const componentRef = useRef();
  const [bankDetails, setBankDetails] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);
  const [isEditing, setIsEditing] = useState(true);

   const API_URL = process.env.REACT_APP_API_URL;

  // Fetch bank details from API
  const fetchBankDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBankDetails(response.data);
      
      // Set default bank if available
      if (response.data.length > 0) {
        const defaultBank = response.data.find(bank => bank.is_default) || response.data[0];
        setSelectedBankId(defaultBank.id);
      }
    } catch (error) {
      console.error('[fetchBankDetails] Error fetching bank details:', error);
      message.error('Failed to fetch bank details');
    }
  }, [API_URL]);

  // Initialize component data
  useEffect(() => {
    if (visible && registration) {
      // Fetch bank details when modal opens
      fetchBankDetails();
      
      // Set default expiration date (7 days from invoice date)
      const invoiceDate = moment(registration.date);
      const defaultExpiration = registration.expiration_date 
        ? moment(registration.expiration_date)
        : invoiceDate.clone().add(3, 'days');
      setExpirationDate(defaultExpiration);
      
      // Set selected bank if provided in registration
      if (registration.bank_id) {
        setSelectedBankId(registration.bank_id);
      }
    }
  }, [visible, registration, fetchBankDetails]);


  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => {

    }
  });

  const handleFinishEditing = () => {
    if (!expirationDate) {
      message.error('Please select an expiration date');
      return;
    }
    if (!selectedBankId) {
      message.error('Please select a bank account');
      return;
    }
    setIsEditing(false);
  };

  const handleEditAgain = () => {
    setIsEditing(true);
  };

  if (!visible || !registration) return null;

  // Generate timestamp for invoice number if not provided
  const timestamp = moment().format('YYYYMMDDHHmm');
  const invoiceNo = registration.invoiceno || `INV-${timestamp}`;

  // Get selected bank details
  const selectedBank = bankDetails.find(bank => bank.id === selectedBankId) || {};
  
  // Bank information with selected bank or fallback defaults
  const bankInfo = {
    bankName: selectedBank.bank_name || 'ธนาคารกสิกรไทย [Kasikorn Bank]',
    accountName: selectedBank.account_name || 'บจก. บริษัท ทีเอฟยู เอ็ดดูเคชั่นแนล เซอวิสเซส',
    accountNumber: selectedBank.account_number || '037-3-80905-5',
    branchName: selectedBank.branch_name || '',
    swiftCode: selectedBank.swift_code || ''
  };

  // Format expiration date
  const formattedExpirationDate = expirationDate ? expirationDate.format('DD MMM YY') : '';

  // Ensure all values are properly parsed as numbers
  const classPrice = parseFloat(registration.price || 0);
  const inventoryTotal = parseFloat(registration.inventory_price || 0);
  const totalBeforeDiscount = classPrice + inventoryTotal;
  const discountAmount = parseFloat(registration.discount || 0);
  const totalAfterDiscount = totalBeforeDiscount - discountAmount;
  const vatAmount = parseFloat(registration.vat || 0);
  const grandTotal = parseFloat(registration.final_price || 0) || (totalAfterDiscount + vatAmount);
  const remainingBalance = parseFloat(registration.remaining_balance || 0);

  return (
    <div className="invoice-modal-overlay">
      <div className="invoice-modal" ref={componentRef}>
        <span className="modal-close-button" onClick={onClose}>&times;</span>
        
        {/* Settings Panel - Only show when editing */}
        {isEditing && (
          <Card 
            title="Invoice Settings" 
            size="small" 
            style={{ 
              marginBottom: 20,
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f'
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Expiration Date:</Text>
                <DatePicker 
                  value={expirationDate}
                  onChange={setExpirationDate}
                  format="DD MMM YY"
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="Select due date"
                />
              </Col>
              <Col span={12}>
                <Text strong>Bank Account:</Text>
                <Select
                  value={selectedBankId}
                  onChange={setSelectedBankId}
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="Select bank account"
                  loading={bankDetails.length === 0}
                >
                  {bankDetails.map(bank => (
                    <Option key={bank.id} value={bank.id}>
                      {bank.bank_name} - {bank.account_number}
                      {bank.is_default && <Text style={{ color: '#52c41a' }}> (Default)</Text>}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Row style={{ marginTop: 16 }}>
              <Col span={24}>
                <Space>
                  <Button type="primary" onClick={handleFinishEditing}>
                    Generate Invoice
                  </Button>
                  <Button onClick={onClose}>
                    Cancel
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        {/* Invoice Content - Only show when not editing */}
        {!isEditing && (
          <>
            
            {/* Edit Button - Top Left */}
            <Button 
              size="small" 
              onClick={handleEditAgain}
              style={{ 
                position: 'absolute', 
                top: 10, 
                left: 50, 
                zIndex: 10 
              }}
            >
              Edit Settings
            </Button>
            
            {/* Header section with logo and company info */}
           <Row justify="space-between" align="top" style={{ marginBottom: 10 }}>
              <Col span={14}>
               <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <img
                  src="/images/plann_log.png"
                  alt="Company Logo"
                  style={{ width: 120, height: 'auto' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <div>
                  <Title level={4} style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '14px' }}>
                    PLANN TECH Co.,Ltd.
                  </Title>
                  <Text style={{ fontSize: 10, display: 'block', lineHeight: '1.4' }}>
                    123 Sukhumvit Rd.,<br />
                    Khlong Tan, Khlong Toey<br />
                    Bangkok 10110<br />
                    TAX ID 0105565096587<br />
                    Tel: 065-2644595<br />
                    Email: info@plann.tech
                  </Text>
                </div>
              </div>
            </Col>
              {/* Right side - Invoice box */}
              <Col span={10}>
              <Card
                size="small"
                style={{
                  backgroundColor: '#f0f5ff',
                  border: '2px solid #d6e4ff',
                  textAlign: 'center',
                  marginLeft: 'auto',
                  maxWidth: 200
                }}
              >
                <Title level={3} className="invoice-title" style={{ marginBottom: 1 }}>
                  {registration.is_refunded ? t('creditNote').toUpperCase() : t('invoice').toUpperCase()}
                </Title>
                <div className="invoice-details" style={{ marginBottom: 1 }}>
                  <div className="detail-row" style={{ marginBottom: 1 }}>
                    <Text strong>{t('No.')}:</Text>
                    <Text>{invoiceNo}</Text>
                  </div>
                  <div className="detail-row" style={{ marginBottom: 1 }}>
                    <Text strong>{t('date')}:</Text>
                    <Text>{formatDate(registration.date, i18n.language)}</Text>
                  </div>
                  <div className="detail-row" style={{ marginBottom: 1 }}>
                    <Text strong>{t('Due Date')}:</Text>
                    <Text style={{ color: '#d4380d' }}>{formattedExpirationDate}</Text>
                  </div>
                </div>
              </Card>
              </Col>
            </Row>
            
            {/* Customer Information */}
            <div className="customer-section">
              <Title level={5} className="section-title">{t('Customer Information')}</Title>
              <Card size="small" className="customer-card">
                <div className="customer-details">
                  <Text><strong>{t('name')}:</strong> {registration.student_name}</Text><br />
                  <Text><strong>{t('address')}:</strong> {registration.address || t('na')}</Text><br />
                  <Text><strong>{t('phone')}:</strong> {registration.phone || t('na')}</Text>
                </div>
              </Card>
            </div>
            
      {/* Invoice Items as Rows */}
            <div className="table-section">
              <Title level={5} style={{ marginBottom: 12 }}>{t('Invoice Items')}</Title>
             <Card 
                size="small" 
                style={{ marginBottom: 16 }}
              >
                {/* Header Row */}
                <div style={{
                  backgroundColor: '#f0f0fa',
                  padding: '5px 0px',
                  borderBottom: '1px solid #d9d9d9',
                  fontSize: 13,
                  fontWeight: 'bold'
                }}>
                  <Row>
                    <Col span={2} style={{ textAlign: 'center' }}>#</Col>
                    <Col span={10}>{t('description')}</Col>
                    <Col span={4} style={{ textAlign: 'right' }}>{t('quantity')}</Col>
                    <Col span={4} style={{ textAlign: 'right' }}>{t('unitPrice')}</Col>
                    <Col span={4} style={{ textAlign: 'right' }}>{t('total')}</Col>
                  </Row>
                </div>

                {/* First Item Row - Course */}
                <div style={{
                  padding: '5px 0px',
                  borderBottom: (registration.inventory_items?.length > 0) ? '1px solid #f0f0f0' : 'none',
                  backgroundColor: 'transparent',
                  fontSize: 13
                }}>
                  <Row align="middle">
                    <Col span={2} style={{ textAlign: 'center' }}>
                      <Text>1</Text>
                    </Col>
                    <Col span={10}>
                      <Text>{registration.class_code}</Text>
                    </Col>
                    <Col span={4} style={{ textAlign: 'right' }}>
                      <Text>1</Text>
                    </Col>
                    <Col span={4} style={{ textAlign: 'right' }}>
                      <Text>฿{formatNumber(classPrice, i18n.language)}</Text>
                    </Col>
                    <Col span={4} style={{ textAlign: 'right' }}>
                      <Text>฿{formatNumber(classPrice, i18n.language)}</Text>
                    </Col>
                  </Row>
                </div>

                {/* Inventory Items - Only Render If Exists */}
                {Array.isArray(registration.inventory_items) &&
                  registration.inventory_items.length > 0 &&
                  registration.inventory_items
                    .filter(item => {
                      const parsed = typeof item === 'string' ? JSON.parse(item) : item;
                      return parsed.label && parsed.price_per_unit > 0;
                    })
                    .map((item, index) => {
                      const parsedItem = typeof item === 'string' ? JSON.parse(item) : item;

                      return (
                        <div
                          key={index}
                          style={{
                            padding: '5px 0px',
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: (index + 1) % 2 === 1 ? '#f8f8ff' : 'transparent',
                            fontSize: 13,
                          }}
                        >
                          <Row align="middle">
                            <Col span={2} style={{ textAlign: 'center' }}>
                              <Text>{index + 2}</Text>
                            </Col>
                            <Col span={10}><Text>{parsedItem.label}</Text></Col>
                            <Col span={4} style={{ textAlign: 'right' }}><Text>1</Text></Col>
                            <Col span={4} style={{ textAlign: 'right' }}><Text>฿{formatNumber(parsedItem.price_per_unit, i18n.language)}</Text></Col>
                            <Col span={4} style={{ textAlign: 'right' }}><Text>฿{formatNumber(parsedItem.price_per_unit, i18n.language)}</Text></Col>
                          </Row>
                        </div>
                      );
                    })}
              </Card>
            </div>
            
            {/* Totals Summary */}
            <Row style={{ marginBottom: 10 }}>
              <Col span={24}>
                <div style={{ 
                  fontSize: 9,
                  border: '1px solid #d9d9d9',
                  padding: 5,
                  backgroundColor: '#fafafa',
                  borderRadius: 6,
                  width: 300,
                  marginLeft: 'auto' 
                }}>
                  <Row justify="space-between" style={{ marginBottom: 2 }}>
                    <Text>{t('subtotal')}:</Text>
                    <Text>{formatNumber(totalBeforeDiscount, i18n.language)}</Text>
                  </Row>
                  <Row justify="space-between" style={{ marginBottom: 2 }}>
                    <Text>{t('discount')}:</Text>
                    <Text style={{ color: 'red' }}>- {formatNumber(discountAmount, i18n.language)}</Text>
                  </Row>
                  <Row justify="space-between" style={{ marginBottom: 2 }}>
                    <Text>{t('Total After Discount')}:</Text>
                    <Text>{formatNumber(totalAfterDiscount, i18n.language)}</Text>
                  </Row>
                  <Row justify="space-between" style={{ marginBottom: 4 }}>
                    <Text>{t('VAT 7%', { percentage: 7 })}:</Text>
                    <Text>{formatNumber(vatAmount, i18n.language)}</Text>
                  </Row>
                  <Divider style={{ margin: '8px 0' }} />
                  <Row justify="space-between">
                    <Text strong style={{ fontSize: 15 }}>{t('grandTotal')}:</Text>
                    <Text strong style={{ fontSize: 15 }}>
                      ฿{formatNumber(grandTotal, i18n.language)}
                    </Text>
                  </Row>
                  {remainingBalance > 0 && (
                    <Row justify="space-between" style={{ marginTop: 4 }}>
                      <Text style={{ color: 'red', fontWeight: 'bold' }}>{t('Remaining Balance')}:</Text>
                      <Text style={{ color: 'red', fontWeight: 'bold' }}>
                        (฿{formatNumber(remainingBalance, i18n.language)})
                      </Text>
                    </Row>
                  )}
                </div>
              </Col>
            </Row>
            
            {/* Payment Information */}
            <div className="payment-section">
              <Title level={5} className="section-title">{t('Payment Information')}</Title>
              <Card size="small" className="payment-card">
                <div className="bank-info">
                  <div className="bank-details">
                    <Text>{bankInfo.bankName}</Text><br />
                    <Text>{bankInfo.accountName}</Text><br />
                    <Text>เลขที่บัญชี {bankInfo.accountNumber}</Text>
                    {bankInfo.branchName && (
                      <>
                        <br />
                        <Text>สาขา {bankInfo.branchName}</Text>
                      </>
                    )}
                    {bankInfo.swiftCode && (
                      <>
                        <br />
                        <Text>SWIFT Code: {bankInfo.swiftCode}</Text>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            </div>
            
          

            <div style={{ marginTop: 48 }}>
              {/* Signature and QR */}
              <Row justify="center" align="bottom" style={{ marginBottom: 24 }}>
                {/* Center: Signature + Seal */}
                <Col span={12}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {/* Authorized Signature */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ height: 80 }} />
                      <div style={{
                        borderTop: '1px solid #333',
                        width: '180px',
                        margin: '0 auto',
                        paddingTop: 4,
                        fontSize: 13,
                        color: '#333'
                      }}>
                        <Text>{t('Authorized Signature')}</Text>
                      </div>
                    </div>

                    {/* Company Seal */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ height: 80 }} />
                      <div style={{
                        borderTop: '1px solid #333',
                        width: '180px',
                        margin: '0 auto',
                        paddingTop: 4,
                        fontSize: 13,
                        color: '#333'
                      }}>
                        <Text>{t('Company Seal')}</Text>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
                {/* Terms and Conditions */}
            <div className="terms-section">
              <Title level={5} style={{ marginBottom: 12 }}>Terms and Conditions</Title>
              <Card 
                size="small" 
                style={{ marginBottom: 24 }}
              >
                <div style={{ fontSize: 10, lineHeight: 1.6 }}>
                  <div style={{ marginBottom: 3 }}>
                    1. Please settle the full tuition fee by {formattedExpirationDate} via bank transfer after receiving your class schedule.
                  </div>
                  <div style={{ marginBottom: 3 }}>
                    2. Please send the transfer slip (or digital slip) together with your nickname, first name, and last name via Line, Facebook, Instagram, or email (info@plann.tech) as proof of payment.
                  </div>
                  <div style={{ marginBottom: 3 }}>
                    3. The school reserves the right to arrange or modify the class schedule if payment is not made within the specified period.
                  </div>
                  <div style={{ marginBottom: 3 }}>
                    4. Once the registration fee has been paid, it is non-transferable and non-refundable.
                  </div>
                  <div style={{ marginBottom: 3 }}>
                    5. In case of emergency, the school may assign a substitute teacher without prior notice.
                  </div>
                  <div style={{ marginBottom: 3 }}>
                    6. A class reschedule or cancellation request must be made at least 24 hours before the scheduled session.
                  </div>
                  <div style={{ marginBottom: 3 }}>
                    7. Registered lesson hours are valid for 24 months (2 years) and will automatically expire upon the due date.
                  </div>
                </div>
              </Card>
            </div>
              
              <Divider className="section-divider" />
              
              {/* Bottom page info with page number */}
              <Row justify="space-between" align="middle">
                <Col>
                  <div style={{ fontSize: 8, color: '#666' }}>
                    Printed On: {moment().format('DD MMM YY')}
                  </div>
                </Col>
                                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <QRCodeCanvas
                      value={invoiceNo}
                      size={50}
                      level="M"
                      fgColor="#999999"
                    />
                    <div style={{ fontSize: 10, marginTop: 4, color: '#999' }}>
                      PLANN INVOICE
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          
            {/* Print Button */}
            <div className="invoice-actions">
              <button 
                onClick={handlePrint}
                className="print-button"
              >
                {t('printInvoice')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoiceModal;